/**
 * Preferences registry — the SHAPE of every runtime-tunable value
 * the app exposes on /dashboard/preferences/settings.
 *
 * Per Kiran 2026-05-14: admin needs a single screen to edit operational
 * tuning values without a redeploy. As of 2026-05-25 the CURRENT values
 * live in CONFIG_KV alone (key `config:preferences:<KEY>`, no expiry) —
 * the D1 `preferences_config` table was dropped. THIS file holds shape +
 * defaults + bounds + UI metadata.
 *
 * Read path (workers):
 *   await getConfig(env, PREFERENCESKEYS.SESSION_TTLSECS, SESSION.TTLSECS)
 *                                                        ^^^^^^^^^^^^^^^^
 *                                                  fallback = code-level default
 *
 * The fallback keeps the worker booting even if KV is briefly unreachable
 * or a key isn't seeded yet — explicit, not silent (the code default and
 * the KV value are the SAME until an admin edits).
 *
 * Adding a new tunable:
 *   1. Add an entry below with key, section, defaultValue, bounds.
 *   2. Replace the consumer's direct import with getConfig() + the
 *      original constant as the fallback. No migration / seed needed —
 *      getConfig returns the default until the key is first edited, and
 *      the Settings screen shows the default for an unwritten key.
 *
 * History: file was originally `system-config.constants.ts` /
 * `SYSTEMCONFIGREGISTRY`. Renamed 2026-05-19 alongside the
 * sidebar group "System" → "Preferences", table rename
 * `system_config` → `preferences_config`, and the move of the API
 * surface from the IAM worker into its own `preferences`
 * worker so the domain is fully decoupled from identity.
 *
 * Per Kiran 2026-05-15 (SOP 15 Ch.15): 14 future-phase RETENTIONDAYS
 * keys + PURGE.BATCHSIZE + PURGE.VACUUMTHRESHOLD were removed
 * alongside the D1 purge worker. Each phase reintroduces its
 * retention knob when its tables ship.
 */

export type PreferenceSection = 'purge' | 'email' | 'pagination' | 'sessions' | 'staff' | 'warehouse' | 'cart' | 'returns' | 'store'
export type PreferenceValueType = 'int' | 'bool' | 'string'

export interface PreferenceEntry {
  readonly key: string
  readonly section: PreferenceSection
  readonly defaultValue: number | boolean | string
  readonly valueType: PreferenceValueType
  readonly min: number | null
  readonly max: number | null
  readonly description: string
  /**
   * Display unit shown next to the value in the Preferences UI.
   * UI-only metadata (not stored in D1) — derived from the key's
   * semantic purpose. Examples: 'days', 'seconds', 'rows',
   * 'attempts'. Empty string for unitless counts where the
   * column already implies the meaning.
   */
  readonly unit: string
  /**
   * True for legally-mandated retention rows (7-year GST + DPDP).
   * UI shows a badge; handler enforces the floor from `min`.
   */
  readonly isLegallyMandated: boolean
}

/**
 * Stable key strings used by getConfig + UI. Each maps to a
 * `config:preferences:<KEY>` entry in CONFIG_KV. Listed here so callers
 * don't sprinkle bare strings.
 */
export const PREFERENCESKEYS = {
  // purge & retention (Supabase only; D1 purge worker removed 2026-05-15;
  // backup worker removed 2026-05-17 — D1 PITR is the sole DB recovery layer)
  RETENTIONDAYS_SUPABASEFLOWLOGS:       'RETENTIONDAYS.SUPABASEFLOWLOGS',
  RETENTIONDAYS_SUPABASEERRORFLAGS:     'RETENTIONDAYS.SUPABASEERRORFLAGS',
  RETENTIONDAYS_SUPABASEAUDITLOG:       'RETENTIONDAYS.SUPABASEAUDITLOG',
  PURGE_MAXRUNTIMESECS:                 'PURGE.MAXRUNTIMESECS',
  // media-purge (added 2026-05-17 with product media)
  MEDIAPURGE_RETENTIONDAYS:             'MEDIAPURGE.RETENTIONDAYS',
  MEDIAPURGE_BATCHSIZE:                 'MEDIAPURGE.BATCHSIZE',
  MEDIAPURGE_ORPHANSCAN_BATCHSIZE:      'MEDIAPURGE.ORPHANSCAN.BATCHSIZE',
  // email & OTP
  EMAILVERIFY_CODETTLSECS:        'EMAILVERIFY.CODETTLSECS',
  EMAILVERIFY_VERIFIEDTTLSECS:    'EMAILVERIFY.VERIFIEDTTLSECS',
  EMAILVERIFY_MAXATTEMPTS:        'EMAILVERIFY.MAXATTEMPTS',
  EMAILVERIFY_RESENDCOOLDOWNSECS: 'EMAILVERIFY.RESENDCOOLDOWNSECS',
  // EMAILVERIFY.CODELENGTH deliberately NOT included — the confirm
  // handler regex /^\d{6}$/ is hardcoded; changing the length would
  // also need UI + regex + email-template updates. Code-only.
  // pagination
  PAGINATION_PAGESIZEDEFAULT: 'PAGINATION.PAGESIZEDEFAULT',
  PAGINATION_PAGESIZEMAX:     'PAGINATION.PAGESIZEMAX',
  // sessions — one TTL for every user (the staff/customer split was
  // removed 2026-05-23; one session model for everyone).
  SESSION_TTLSECS:             'SESSION.TTLSECS',
  SESSION_REFRESHTHRESHOLDSECS: 'SESSION.REFRESHTHRESHOLDSECS',
  // staff
  STAFF_WELCOMETOKENTTLSECS: 'STAFF.WELCOMETOKENTTLSECS',
  // warehouse
  WAREHOUSE_LOWSTOCKTHRESHOLD: 'WAREHOUSE.LOWSTOCKTHRESHOLD',
  // cart
  CART_ABANDONEDTHRESHOLDDAYS: 'CART.ABANDONEDTHRESHOLDDAYS',
  // returns
  RETURNWINDOWDAYS: 'RETURNS.WINDOWDAYS',
  // cancel — customer can cancel an order in 'confirmed' or 'packed'
  // within this many days from placed_at (Kiran 2026-06-19).
  CANCELWINDOWDAYS: 'CANCEL.WINDOWDAYS',
  // store — toggle for whether this deployment runs as an e-commerce
  // storefront (cart + checkout) or as a catalog-only / non-selling
  // brand site. Default true (current store IS e-commerce). Consumed
  // by storefront UI gates via getConfig() so a flip takes effect
  // without a redeploy.
  STORE_ISECOMMERCE: 'STORE.ISECOMMERCE',
  // Operator BCC address — every cart-quote email the customer
  // requests on /cart goes to them with a copy here so the operator
  // can follow up. Empty = no BCC. Kiran 2026-06-28.
  STORE_OPERATOREMAIL: 'STORE.OPERATOREMAIL'
} as const
export type PreferenceKey = (typeof PREFERENCESKEYS)[keyof typeof PREFERENCESKEYS]

/**
 * Display labels per section for the Preferences UI tabs.
 */
export const PREFERENCESSECTIONLABELS: Record<PreferenceSection, string> = {
  purge:      'Purge & Retention',
  email:      'Email & OTP',
  pagination: 'Pagination',
  sessions:   'Session TTLs',
  staff:      'Staff',
  warehouse:  'Warehouse',
  cart:       'Cart',
  returns:    'Returns',
  store:      'Store'
}

/**
 * Registry of every tunable. CONFIG_KV holds the current values at
 * runtime; this is the source of truth for shape (type, bounds,
 * description, mandated flag) + the default each key falls back to.
 */
export const PREFERENCESREGISTRY: ReadonlyArray<PreferenceEntry> = [
  // ---------- purge (Supabase observability only) ----------
  { key: 'RETENTIONDAYS.SUPABASEFLOWLOGS',        section: 'purge', defaultValue: 90,   valueType: 'int', min: 7,    max: 365,   unit: 'days',     description: 'Days to retain Supabase flow_logs (step trail) before hard-delete.',                  isLegallyMandated: false },
  { key: 'RETENTIONDAYS.SUPABASEERRORFLAGS',      section: 'purge', defaultValue: 90,   valueType: 'int', min: 7,    max: 365,   unit: 'days',     description: 'Days to retain resolved Supabase error_flags before hard-delete.',                    isLegallyMandated: false },
  { key: 'RETENTIONDAYS.SUPABASEAUDITLOG',        section: 'purge', defaultValue: 2555, valueType: 'int', min: 2555, max: 3650,  unit: 'days',     description: 'Days to retain Supabase audit_log (7-year compliance — floor-bounded).',              isLegallyMandated: true  },
  { key: 'PURGE.MAXRUNTIMESECS',                  section: 'purge', defaultValue: 1500, valueType: 'int', min: 60,   max: 1700,  unit: 'seconds',  description: 'Maximum total runtime before supabase-purge aborts. CF scheduled-worker hard limit is 1800.', isLegallyMandated: false },
  { key: 'MEDIAPURGE.RETENTIONDAYS',              section: 'purge', defaultValue: 30,   valueType: 'int', min: 1,    max: 365,   unit: 'days',     description: 'Days after a media row is soft-deleted before the media-purge worker hard-deletes the R2 bytes + D1 row.', isLegallyMandated: false },
  { key: 'MEDIAPURGE.BATCHSIZE',                  section: 'purge', defaultValue: 100,  valueType: 'int', min: 10,   max: 1000,  unit: 'rows',     description: 'Maximum soft-deleted media rows the media-purge worker processes per scheduled tick.', isLegallyMandated: false },
  { key: 'MEDIAPURGE.ORPHANSCAN.BATCHSIZE',       section: 'purge', defaultValue: 500,  valueType: 'int', min: 50,   max: 5000,  unit: 'rows',     description: 'Maximum R2 keys scanned per orphan-sweep tick (runs Mondays inside the daily media-purge cron).', isLegallyMandated: false },

  // ---------- email ----------
  { key: 'EMAILVERIFY.CODETTLSECS',          section: 'email', defaultValue: 300, valueType: 'int', min: 60,  max: 1800, unit: 'seconds',  description: 'Seconds a verification code stays valid before expiry.',                                        isLegallyMandated: false },
  { key: 'EMAILVERIFY.VERIFIEDTTLSECS',      section: 'email', defaultValue: 900, valueType: 'int', min: 120, max: 3600, unit: 'seconds',  description: 'Seconds the post-verification marker stays in KV (handoff from verify-confirm to register).',  isLegallyMandated: false },
  { key: 'EMAILVERIFY.MAXATTEMPTS',          section: 'email', defaultValue: 3,   valueType: 'int', min: 1,   max: 10,   unit: 'attempts', description: 'Max wrong code attempts before the user must request a new code.',                              isLegallyMandated: false },
  { key: 'EMAILVERIFY.RESENDCOOLDOWNSECS',   section: 'email', defaultValue: 60,  valueType: 'int', min: 60,  max: 600,  unit: 'seconds',  description: 'Minimum delay between consecutive verify-request calls. Cloudflare KV requires ≥ 60.',         isLegallyMandated: false },

  // ---------- pagination ----------
  { key: 'PAGINATION.PAGESIZEDEFAULT',  section: 'pagination', defaultValue: 25,  valueType: 'int', min: 5,  max: 200, unit: 'rows', description: 'Default rows per page when the client does not specify ?pageSize.',         isLegallyMandated: false },
  { key: 'PAGINATION.PAGESIZEMAX',      section: 'pagination', defaultValue: 100, valueType: 'int', min: 25, max: 500, unit: 'rows', description: 'Maximum rows per page. Requests above this are clamped to this value.',    isLegallyMandated: false },

  // ---------- sessions ----------
  { key: 'SESSION.TTLSECS',                 section: 'sessions', defaultValue: 604800, valueType: 'int', min: 3600, max: 2592000, unit: 'seconds', description: 'Seconds a session cookie stays valid (every user). Default 7 days.',                  isLegallyMandated: false },
  { key: 'SESSION.REFRESHTHRESHOLDSECS',    section: 'sessions', defaultValue: 86400,  valueType: 'int', min: 300,  max: 604800,  unit: 'seconds', description: 'Refresh the session when remaining lifetime falls below this. Default 1 day.', isLegallyMandated: false },

  // ---------- staff ----------
  { key: 'STAFF.WELCOMETOKENTTLSECS', section: 'staff', defaultValue: 86400, valueType: 'int', min: 3600, max: 604800, unit: 'seconds', description: 'Seconds the one-time welcome link emailed to a new staff member stays valid. Default 24 hours. If expired, admin re-issues by triggering a re-send.', isLegallyMandated: false },

  // ---------- warehouse ----------
  { key: 'WAREHOUSE.LOWSTOCKTHRESHOLD', section: 'warehouse', defaultValue: 5, valueType: 'int', min: 0, max: 1000, unit: 'units', description: 'A variant with on-hand stock at or below this is flagged "Low stock" on the Warehouse screens.', isLegallyMandated: false },

  // ---------- cart ----------
  { key: 'CART.ABANDONEDTHRESHOLDDAYS', section: 'cart', defaultValue: 3, valueType: 'int', min: 1, max: 90, unit: 'days', description: 'A cart with items that has not been updated for this many days is counted as Abandoned on the dashboard’s Carts tab. Carts in the [24h, this threshold) window are counted as Stale instead.', isLegallyMandated: false },

  // ---------- returns ----------
  { key: 'RETURNS.WINDOWDAYS', section: 'returns', defaultValue: 10, valueType: 'int', min: 1, max: 90, unit: 'days', description: 'Days after delivery during which a customer can file a return on /account/orders. Once exceeded, the Return button hides and the order auto-completes via the daily cron.', isLegallyMandated: false },
  { key: 'CANCEL.WINDOWDAYS',  section: 'returns', defaultValue: 2,  valueType: 'int', min: 1, max: 30, unit: 'days', description: 'Days after order placement during which a customer can cancel a not-yet-shipped order on /account/orders. Once the order moves to shipped or the window expires, the Cancel button hides.', isLegallyMandated: false },

  // ---------- store ----------
  { key: 'STORE.ISECOMMERCE',   section: 'store', defaultValue: true, valueType: 'bool',   min: null, max: null, unit: '',  description: 'Whether this deployment runs as an e-commerce storefront (cart + Confirm order). Switch to No for a catalog-only / brand-only site.', isLegallyMandated: false },
  { key: 'STORE.OPERATOREMAIL', section: 'store', defaultValue: '',   valueType: 'string', min: null, max: null, unit: '',  description: 'BCC address for customer-requested cart-quote emails. Blank = no BCC sent (customer still gets their quote). Set to e.g. orders@uyyamadilu.com to keep the operator in the loop.', isLegallyMandated: false }
]

/**
 * Lookup helper — case-sensitive exact match. Returns null if the key
 * is not in the registry (which means it's not editable). Handlers
 * reject unknown keys before any D1 write.
 */
export function findPreferenceEntry(key: string): PreferenceEntry | null {
  return PREFERENCESREGISTRY.find((e) => e.key === key) ?? null
}

/** KV key prefix for preference values (`config:preferences:<KEY>`). */
export const PREFERENCESKVPREFIX = 'config:preferences:'
